import React from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import * as LucideIcons from 'lucide-react';

const SalesFilterToolbar = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  onCreateNew, 
  customerOptions, 
  statusOptions,
  activeTab 
}) => {
  const getCreateButtonText = () => {
    switch (activeTab) {
      case 'customers': return 'Add Customer';
      case 'quotes': return 'Create Quote';
      case 'retainer-invoices': return 'Create Retainer';
      case 'invoices': return 'Create Invoice';
      case 'recurring-invoices': return 'Setup Recurring';
      case 'delivery-challans': return 'Create Challan';
      case 'payment-links': return 'Generate Link';
      case 'payments-received': return 'Record Payment';
      case 'credit-notes': return 'Issue Credit Note';
      default: return 'Create New';
    }
  };

  const getCreateButtonIcon = () => {
    switch (activeTab) {
      case 'customers': return 'UserPlus';
      case 'quotes': return 'FileText';
      case 'retainer-invoices': return 'Wallet';
      case 'invoices': return 'Receipt';
      case 'recurring-invoices': return 'RotateCcw';
      case 'delivery-challans': return 'Truck';
      case 'payment-links': return 'Link';
      case 'payments-received': return 'CreditCard';
      case 'credit-notes': return 'FileCheck';
      default: return 'Plus';
    }
  };

  // Filter status options based on active tab
  const getFilteredStatusOptions = () => {
    switch (activeTab) {
      case 'customers':
        return statusOptions?.filter(opt => ['active', 'inactive']?.includes(opt?.value));
      case 'quotes':
        return statusOptions?.filter(opt => ['draft', 'sent', 'accepted', 'declined', 'expired']?.includes(opt?.value));
      case 'retainer-invoices':
        return statusOptions?.filter(opt => ['unused', 'partially-utilized', 'fully-utilized']?.includes(opt?.value));
      case 'invoices':
        return statusOptions?.filter(opt => ['draft', 'sent', 'paid', 'partially-paid', 'overdue', 'cancelled']?.includes(opt?.value));
      case 'recurring-invoices':
        return statusOptions?.filter(opt => ['active', 'inactive']?.includes(opt?.value));
      case 'delivery-challans':
        return statusOptions?.filter(opt => ['pending', 'in-transit', 'delivered']?.includes(opt?.value));
      case 'payment-links':
        return statusOptions?.filter(opt => ['sent', 'paid', 'expired', 'cancelled']?.includes(opt?.value));
      case 'payments-received':
        return statusOptions?.filter(opt => ['cleared', 'pending-clearance', 'failed']?.includes(opt?.value));
      case 'credit-notes':
        return statusOptions?.filter(opt => ['issued', 'applied']?.includes(opt?.value));
      default:
        return statusOptions;
    }
  };

  const hasAmountFilter = !['customers', 'delivery-challans']?.includes(activeTab);

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm p-6">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left side - Filters */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {/* Customer Filter */}
          <div className="col-span-1">
            <Select
              value={filters?.customer || ''}
              onValueChange={(value) => onFilterChange('customer', value)}
              placeholder="All Customers"
              options={[{ value: '', label: 'All Customers' }, ...customerOptions]}
            />
          </div>

          {/* Status Filter */}
          <div className="col-span-1">
            <Select
              value={filters?.status || ''}
              onValueChange={(value) => onFilterChange('status', value)}
              placeholder="All Statuses"
              options={[{ value: '', label: 'All Statuses' }, ...getFilteredStatusOptions()]}
            />
          </div>

          {/* Date From */}
          <div className="col-span-1">
            <Input
              type="date"
              value={filters?.dateFrom || ''}
              onChange={(e) => onFilterChange('dateFrom', e?.target?.value)}
              placeholder="From Date"
            />
          </div>

          {/* Date To */}
          <div className="col-span-1">
            <Input
              type="date"
              value={filters?.dateTo || ''}
              onChange={(e) => onFilterChange('dateTo', e?.target?.value)}
              placeholder="To Date"
            />
          </div>

          {/* Amount Filters - only for documents with amounts */}
          {hasAmountFilter && (
            <>
              <div className="col-span-1">
                <Input
                  type="number"
                  value={filters?.minAmount || ''}
                  onChange={(e) => onFilterChange('minAmount', e?.target?.value)}
                  placeholder="Min Amount"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="col-span-1">
                <Input
                  type="number"
                  value={filters?.maxAmount || ''}
                  onChange={(e) => onFilterChange('maxAmount', e?.target?.value)}
                  placeholder="Max Amount"
                  min="0"
                  step="0.01"
                />
              </div>
            </>
          )}
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onClearFilters}
            iconName="X"
            iconPosition="left"
            size="sm"
          >
            Clear
          </Button>
          
          <Button
            variant="default"
            onClick={onCreateNew}
            iconName={getCreateButtonIcon()}
            iconPosition="left"
          >
            {getCreateButtonText()}
          </Button>
        </div>
      </div>
      {/* Active Filters Display */}
      {(filters?.customer || filters?.status || filters?.dateFrom || filters?.dateTo || filters?.minAmount || filters?.maxAmount) && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-muted-foreground">Active filters:</span>
            
            {filters?.customer && (
              <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                <span>Customer: {customerOptions?.find(c => c?.value === filters?.customer)?.label}</span>
                <button
                  onClick={() => onFilterChange('customer', '')}
                  className="hover:bg-primary/20 rounded-full p-0.5"
                >
                  <LucideIcons.X size={12} />
                </button>
              </div>
            )}
            
            {filters?.status && (
              <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                <span>Status: {getFilteredStatusOptions()?.find(s => s?.value === filters?.status)?.label}</span>
                <button
                  onClick={() => onFilterChange('status', '')}
                  className="hover:bg-primary/20 rounded-full p-0.5"
                >
                  <LucideIcons.X size={12} />
                </button>
              </div>
            )}
            
            {(filters?.dateFrom || filters?.dateTo) && (
              <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                <span>
                  Date: {filters?.dateFrom || 'Start'} - {filters?.dateTo || 'End'}
                </span>
                <button
                  onClick={() => {
                    onFilterChange('dateFrom', '');
                    onFilterChange('dateTo', '');
                  }}
                  className="hover:bg-primary/20 rounded-full p-0.5"
                >
                  <LucideIcons.X size={12} />
                </button>
              </div>
            )}
            
            {(filters?.minAmount || filters?.maxAmount) && hasAmountFilter && (
              <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                <span>
                  Amount: ${filters?.minAmount || '0'} - ${filters?.maxAmount || '∞'}
                </span>
                <button
                  onClick={() => {
                    onFilterChange('minAmount', '');
                    onFilterChange('maxAmount', '');
                  }}
                  className="hover:bg-primary/20 rounded-full p-0.5"
                >
                  <LucideIcons.X size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesFilterToolbar;